/** Shared helpers for Practice Track resume / continue UX. */

export const LAST_ACTIVE_PROBLEM_KEY = "lastActiveProblem";

export const PRACTICE_SECTION_ORDER = [
  "Getting Started",
  "Patterns",
  "Number System",
  "Arrays",
  "2-D Array",
  "Strings",
  "Recursion & Backtracking",
  "Time & Space Complexity",
  "Stack and Queues",
  "Linked List",
  "Generic Trees",
  "Binary Trees",
  "Binary Search Trees",
  "HashMap & Heap",
  "Graph",
  "Dynamic Programming",
];

export const saveLastActiveProblem = (problem) => {
  if (!problem?.slug) return;
  try {
    localStorage.setItem(
      LAST_ACTIVE_PROBLEM_KEY,
      JSON.stringify({
        slug: problem.slug,
        title: problem.title,
        level: problem.level,
        section: problem.section,
        visitedAt: Date.now(),
      }),
    );
    if (problem.level != null && problem.level !== "") {
      localStorage.setItem("selectedLevel", String(problem.level));
    }
    if (problem.section) {
      localStorage.setItem("selectedSection", problem.section);
    }
  } catch {
    // ignore quota / private mode
  }
};

export const getLastActiveProblem = () => {
  try {
    const raw = localStorage.getItem(LAST_ACTIVE_PROBLEM_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const sortTrackProblems = (list) =>
  [...list].sort((a, b) => {
    const levelDiff = Number(a.level) - Number(b.level);
    if (levelDiff !== 0) return levelDiff;
    const sectionDiff =
      PRACTICE_SECTION_ORDER.indexOf(a.section) -
      PRACTICE_SECTION_ORDER.indexOf(b.section);
    if (sectionDiff !== 0) return sectionDiff;
    return (
      (a.displayOrder || 0) - (b.displayOrder || 0) ||
      String(a._id || "").localeCompare(String(b._id || ""))
    );
  });

/** True when the user has solved at least one problem or opened one recently. */
export const hasStartedPractice = (problems) => {
  if (problems?.some((p) => p.isSolved)) return true;
  const lastActive = getLastActiveProblem();
  if (!lastActive?.slug) return false;
  return (
    problems?.some((p) => p.slug === lastActive.slug) ?? Boolean(lastActive)
  );
};

/**
 * Best problem to resume — last visited (if still unsolved), then saved section,
 * then first unsolved in track order. Returns null if user hasn't started yet.
 */
export const findContinueTarget = (problems) => {
  if (!problems?.length || !hasStartedPractice(problems)) return null;

  const lastActive = getLastActiveProblem();
  if (lastActive?.slug) {
    const match = problems.find((p) => p.slug === lastActive.slug);
    if (match && !match.isSolved) return match;
  }

  const level = localStorage.getItem("selectedLevel");
  const section = localStorage.getItem("selectedSection");
  if (level && section) {
    const inSection = problems
      .filter(
        (p) =>
          String(p.level) === level && p.section === section && !p.isSolved,
      )
      .sort(
        (a, b) =>
          (a.displayOrder || 0) - (b.displayOrder || 0) ||
          String(a._id || "").localeCompare(String(b._id || "")),
      );
    if (inSection[0]) return inSection[0];
  }

  const trackProblems = sortTrackProblems(
    problems.filter((p) => p.level && p.section && !p.isSolved),
  );
  return trackProblems[0] || null;
};

/** First unsolved problem within a practice-track section list. */
export const findContinueInSection = (sectionProblems) => {
  if (!sectionProblems?.length) return null;

  const lastActive = getLastActiveProblem();
  if (lastActive?.slug) {
    const match = sectionProblems.find(
      (p) => p.slug === lastActive.slug && !p.isSolved,
    );
    if (match) return match;
  }

  return sectionProblems.find((p) => !p.isSolved) || null;
};
